import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { Building2, Search, Edit2, X, Save, Loader2, MapPin, Euro, Calendar, User, Phone, FileText, Filter, Download } from 'lucide-react';
import { groupByProvince } from '../utils/portfolioSort';
import { ColumnPicker, useVisibleColumns } from '../components/app/ColumnPicker';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('es-ES').format(n || 0);
const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const dateStr = (v) => (v ? String(v).slice(0, 10) : '—');

// Definición de columnas seleccionables. Cada `render` recibe el site y
// devuelve el <td> (para poder alinear a derecha, colorear, etc.).
const SITE_COLUMNS = [
  { key: 'code', label: 'Código', defaultVisible: true, always: true,
    render: (s) => (
      <td className="px-3 py-2" key="code">
        <div className="font-mono text-blue-200">{s.code || s.name || '—'}</div>
        {s.alt_code && <div className="text-white/40 text-[10px]">{s.alt_code}</div>}
      </td>
    ),
  },
  { key: 'address', label: 'Dirección', defaultVisible: true,
    render: (s) => <td className="px-3 py-2 text-white/70 max-w-xs truncate" title={s.address} key="address">{s.address || '—'}</td>,
  },
  { key: 'muni_prov', label: 'Municipio · Provincia', defaultVisible: true,
    render: (s) => <td className="px-3 py-2 text-white/70" key="muni">{s.municipality || '—'} <span className="text-white/40">· {s.province || '—'}</span></td>,
  },
  { key: 'owner', label: 'Propietario', defaultVisible: true,
    render: (s) => (
      <td className="px-3 py-2 text-white/70" key="owner">
        {s.owner_name || <span className="text-white/30">—</span>}
        {s.owner_phone && <div className="text-white/40 text-[10px]">📞 {s.owner_phone}</div>}
      </td>
    ),
  },
  { key: 'rent', label: 'Renta €/año', defaultVisible: true, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right font-black text-[#A3FF12]" key="rent">{s.current_rent ? eur(s.current_rent) : '—'}</td>,
  },
  { key: 'contract_end', label: 'Vencimiento', defaultVisible: true,
    render: (s) => <td className="px-3 py-2" key="cend">{dateStr(s.contract_end)}</td>,
  },
  { key: 'status', label: 'Estado', defaultVisible: true,
    render: (s) => <td className="px-3 py-2" key="status"><span className="text-[10px] uppercase font-bold bg-white/5 border border-white/10 rounded-full px-2 py-0.5">{s.status || 'prospeccion'}</span></td>,
  },
  // Opcionales (ocultas por defecto):
  { key: 'contract_signed_at', label: 'Firma contrato', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70" key="sign">{dateStr(s.contract_signed_at)}</td>,
  },
  { key: 'contract_type', label: 'Tipo contrato', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70 text-[11px]" key="ctype">{s.contract_type || '—'}</td>,
  },
  { key: 'superficie', label: 'Superficie m²', defaultVisible: false, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right text-white/70" key="sup">{s.superficie_m2 ? fmt(s.superficie_m2) : '—'}</td>,
  },
  { key: 'fianza', label: 'Fianza (€)', defaultVisible: false, align: 'right',
    render: (s) => <td className="px-3 py-2 text-right text-white/70" key="fianza">{s.fianza_eur ? eur(s.fianza_eur) : '—'}</td>,
  },
  { key: 'catastro', label: 'Ref. catastral', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 font-mono text-[11px] text-white/60" key="cat">{s.catastro_ref || '—'}</td>,
  },
  { key: 'contract_ref', label: 'Ref. contrato', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 font-mono text-[11px] text-white/60" key="cref">{s.contract_ref || '—'}</td>,
  },
  { key: 'priority', label: 'Prioridad', defaultVisible: false,
    render: (s) => <td className="px-3 py-2" key="prio"><span className="text-[10px] uppercase font-bold text-white/60">{s.priority || 'L3'}</span></td>,
  },
  { key: 'lat_lng', label: 'Lat · Lng', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[10px] font-mono text-white/50" key="ll">{s.lat && s.lng ? `${Number(s.lat).toFixed(4)}, ${Number(s.lng).toFixed(4)}` : '—'}</td>,
  },
  { key: 'revalorization', label: 'Mes revaloriz.', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70 text-[11px]" key="rev">{s.revalorization_month || '—'}</td>,
  },
  { key: 'email', label: 'Email propietario', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-white/70 text-[11px]" key="em">{s.owner_email || '—'}</td>,
  },
  { key: 'nif', label: 'NIF propietario', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 font-mono text-[11px] text-white/60" key="nif">{s.owner_nif || '—'}</td>,
  },
  { key: 'estado_totem', label: 'Estado TOTEM', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="et">{s.estado_totem || '—'}</td>,
  },
  { key: 'categoria', label: 'Categoría', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="cat2">{s.categoria_site || '—'}</td>,
  },
  { key: 'infraestructura', label: 'Infraestructura', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="inf">{s.tipo_infraestructura || '—'}</td>,
  },
  { key: 'zona_totem', label: 'Zona TOTEM', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] font-mono text-white/60" key="zn">{s.zona_totem || '—'}</td>,
  },
  { key: 'griet', label: 'Resp. GRIET', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="gr">{s.responsable_griet || '—'}</td>,
  },
  { key: 'propiedad_torre', label: 'Propiedad torre', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="pt">{s.propiedad_torre || '—'}</td>,
  },
  { key: 'fecha_rfi', label: 'Fecha RFI', defaultVisible: false,
    render: (s) => <td className="px-3 py-2 text-[11px] text-white/70" key="rfi">{dateStr(s.fecha_rfi)}</td>,
  },
];

const Emplazamientos = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [prov, setProv] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [ownerFilter, setOwnerFilter] = useState(''); // '' | 'missing' | 'present'
  const [openId, setOpenId] = useState(null);
  const [params] = useSearchParams();
  const [visibleCols, setVisibleCols] = useVisibleColumns(SITE_COLUMNS, 'adquisiciones.cols.v1');
  const activeCols = useMemo(() => SITE_COLUMNS.filter((c) => visibleCols.has(c.key)), [visibleCols]);
  const colSpan = activeCols.length + 1; // + acción
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (prov) params.set('prov', prov);
      if (status) params.set('status', status);
      if (source) params.set('source', source);
      if (ownerFilter) params.set('owner', ownerFilter);
      if (q.trim()) params.set('q', q.trim());
      const res = await api.get(`/sites/export.xlsx?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : `emplazamientos_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Error al exportar');
    } finally { setExporting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sites');
      setItems(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const focus = params.get('id');
    if (focus) setOpenId(focus);
    const monthFilter = params.get('expiring');
    if (monthFilter) setQ(monthFilter);
    const provParam = params.get('prov');
    if (provParam) setProv(provParam);
    const ownerParam = params.get('owner');
    if (ownerParam === 'missing' || ownerParam === 'present') setOwnerFilter(ownerParam);
  }, [params]);

  const provinces = useMemo(() => Array.from(new Set(items.map((s) => s.province).filter(Boolean))).sort(), [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((s) => {
      if (prov && s.province !== prov) return false;
      if (status && s.status !== status) return false;
      if (source === 'totem' && s.source !== 'totem_import') return false;
      if (source === 'manual' && s.source === 'totem_import') return false;
      const hasOwner = !!(s.owner_name || s.owner_phone);
      if (ownerFilter === 'missing' && hasOwner) return false;
      if (ownerFilter === 'present' && !hasOwner) return false;
      if (!term) return true;
      const hay = [s.code, s.alt_code, s.name, s.address, s.municipality, s.province, s.owner_name, s.owner_phone, s.contract_end, s.catastro_ref, s.contract_ref].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, prov, status, source, ownerFilter]);

  const totals = useMemo(() => ({
    count: filtered.length,
    rent_annual: filtered.reduce((a, s) => a + (Number(s.current_rent) || 0), 0),
    with_contract_end: filtered.filter((s) => s.contract_end).length,
    without_owner: filtered.filter((s) => !(s.owner_name || s.owner_phone)).length,
    without_owner_total: items.filter((s) => !(s.owner_name || s.owner_phone)).length,
  }), [filtered, items]);

  // Agrupamos por provincia (A→Z, "Sin provincia" al final) y dentro de cada
  // grupo ordenamos por código TOTEM numérico ascendente. Cap de 500 filas
  // repartido proporcionalmente para no reventar el DOM.
  const CAP = 500;
  const provinceGroups = useMemo(() => {
    const groups = groupByProvince(filtered);
    if (filtered.length <= CAP) return groups;
    // proporcional
    const out = [];
    let used = 0;
    groups.forEach((g, i) => {
      const share = Math.max(1, Math.round(g.items.length * CAP / filtered.length));
      const take = i === groups.length - 1 ? CAP - used : Math.min(share, g.items.length);
      out.push({ ...g, items: g.items.slice(0, take), truncated: g.items.length > take });
      used += take;
    });
    return out;
  }, [filtered]);

  return (
    <div className="p-4 sm:p-8 max-w-[1600px] mx-auto" data-testid="emplazamientos-page">
      <div className="mb-4">
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">PORTFOLIO · EMPLAZAMIENTOS</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Emplazamientos</h1>
        <p className="text-white/50 text-sm mt-1">
          {fmt(totals.count)} de {fmt(items.length)} · {eur(totals.rent_annual)}/año en cartera · {fmt(totals.with_contract_end)} con vencimiento registrado.
        </p>
        {/* KPI propietarios pendientes: click para filtrar */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setOwnerFilter((f) => (f === 'missing' ? '' : 'missing'))}
            data-testid="kpi-owner-missing"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-black tracking-wide transition-colors ${
              ownerFilter === 'missing'
                ? 'bg-orange-500/20 border-orange-500/50 text-orange-200'
                : 'bg-orange-500/5 border-orange-500/25 text-orange-300 hover:bg-orange-500/15'
            }`}
          >
            <User size={12}/>
            Propietarios pendientes · {fmt(totals.without_owner_total)}
            {ownerFilter === 'missing' && <X size={11} className="opacity-60"/>}
          </button>
          {ownerFilter === 'missing' && (
            <span className="text-[11px] text-white/40">Mostrando {fmt(totals.without_owner)} sin propietario</span>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-black/30 border border-white/10 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-white/40"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar código, dirección, catastro, propietario, YYYY-MM…"
            data-testid="site-search"
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white flex-1"/>
        </div>
        <Filter size={14} className="text-white/40"/>
        <select value={prov} onChange={(e) => setProv(e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Todas las provincias</option>
          {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Cualquier estado</option>
          <option value="prospeccion">Prospección</option>
          <option value="negociacion">Negociación</option>
          <option value="firmado">Firmado</option>
          <option value="descartado">Descartado</option>
        </select>
        <div className="flex bg-black/40 border border-white/10 rounded-lg overflow-hidden">
          <button onClick={() => setSource('')} className={`px-2.5 py-1.5 text-[11px] font-bold ${source==='' ? 'bg-white/10 text-white' : 'text-white/50'}`}>Todos</button>
          <button onClick={() => setSource('totem')} className={`px-2.5 py-1.5 text-[11px] font-bold ${source==='totem' ? 'bg-blue-500/30 text-blue-100' : 'text-white/50'}`}>TOTEM</button>
          <button onClick={() => setSource('manual')} className={`px-2.5 py-1.5 text-[11px] font-bold ${source==='manual' ? 'bg-[#A3FF12]/20 text-[#A3FF12]' : 'text-white/50'}`}>Manual</button>
        </div>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} data-testid="owner-filter"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Con o sin propietario</option>
          <option value="present">Con propietario</option>
          <option value="missing">Sin propietario</option>
        </select>
        <ColumnPicker columns={SITE_COLUMNS} value={visibleCols} onChange={setVisibleCols} storageKey="adquisiciones.cols.v1"/>
        <button
          onClick={doExport}
          disabled={exporting || loading}
          data-testid="btn-export-sites"
          className="flex items-center gap-1.5 bg-[#A3FF12]/15 border border-[#A3FF12]/40 hover:bg-[#A3FF12]/25 text-[#A3FF12] font-black text-[11px] px-3 py-1.5 rounded-lg disabled:opacity-50"
          title="Descargar Excel con los sites filtrados"
        >
          {exporting ? <Loader2 className="animate-spin" size={13}/> : <Download size={13}/>}
          Exportar Excel
        </button>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-white/40" size={30}/></div>
      ) : (
        <div className="bg-black/30 border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-white/50 uppercase text-[10px] border-b border-white/10">
              <tr>
                {activeCols.map((c) => (
                  <th key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                ))}
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {provinceGroups.map((group) => (
                <React.Fragment key={group.province}>
                  <tr className="bg-white/[0.04] border-t border-white/10 sticky" data-testid={`province-header-${group.province}`}>
                    <td colSpan={colSpan} className="px-3 py-2">
                      <div className="flex items-center gap-2 text-[11px] font-black tracking-widest text-[#A3FF12]">
                        <MapPin size={12}/>
                        <span className="uppercase">{group.province}</span>
                        <span className="text-white/40 font-mono">· {fmt(group.items.length)}{group.truncated ? '+' : ''}</span>
                      </div>
                    </td>
                  </tr>
                  {group.items.map((s) => (
                    <tr key={s.id} className="border-t border-white/5 hover:bg-white/5">
                      {activeCols.map((c) => c.render(s))}
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setOpenId(s.id)} data-testid={`edit-site-${s.id}`}
                          className="text-blue-300 hover:text-blue-100 font-bold flex items-center gap-1 ml-auto">
                          <Edit2 size={13}/> Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {filtered.length > CAP && (
                <tr><td colSpan={colSpan} className="text-center text-white/40 py-3">Mostrando {fmt(CAP)} de {fmt(filtered.length)}. Refina búsqueda por provincia o texto para ver más.</td></tr>
              )}
              {filtered.length === 0 && (
                <tr><td colSpan={colSpan} className="text-center text-white/40 py-8">Sin resultados con estos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openId && <SiteEditor id={openId} onClose={() => setOpenId(null)} onSaved={(s) => { setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, ...s } : x)); setOpenId(null); }}/>}
    </div>
  );
};


// Campos aislados fuera del componente padre: evita re-crear el tipo de componente
// en cada render (lo que provocaba pérdida de foco y scroll saltarín al teclear).
const F = ({ label, k, type = 'text', value, onChange, ...rest }) => {
  // Los <input type="date"> sólo aceptan `YYYY-MM-DD`. Recorta cualquier ISO
  // con hora que llegue del backend para evitar reformateos que provocaban
  // el salto de scroll y pérdida de foco al teclear.
  const v = type === 'date' && typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : (value ?? '');
  // Para `type=number`: la rueda del ratón cambia el valor Y hace scroll del
  // modal; los spinners nativos también saltan el foco. Los desactivamos para
  // que el formulario se quede totalmente estático mientras se edita.
  const isNumber = type === 'number';
  const numberProps = isNumber ? {
    onWheel: (e) => e.currentTarget.blur(),
    onKeyDown: (e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); },
  } : {};
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-white/50">{label}</span>
      <input type={type} value={v} onChange={(e) => onChange(k, e.target.value)}
        data-testid={`field-${k}`}
        style={isNumber ? { MozAppearance: 'textfield' } : undefined}
        className={`bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#A3FF12]/50 ${isNumber ? '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none' : ''}`}
        {...numberProps}
        {...rest}/>
    </label>
  );
};
const T = ({ label, k, rows = 2, value, onChange }) => (
  <label className="flex flex-col gap-1 md:col-span-2">
    <span className="text-[10px] uppercase tracking-wider text-white/50">{label}</span>
    <textarea value={value ?? ''} onChange={(e) => onChange(k, e.target.value)}
      rows={rows} className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#A3FF12]/50"/>
  </label>
);

const SiteEditor = ({ id, onClose, onSaved }) => {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/sites/${id}`).then(({ data }) => setSite(data)).catch((e) => setErr(e.message));
  }, [id]);

  const setField = useCallback((k, v) => setSite((prev) => ({ ...prev, [k]: v })), []);

  if (!site) return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="text-white/60 flex items-center gap-2"><Loader2 className="animate-spin"/> Cargando…</div>
    </div>
  );

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      // Enviamos solo los campos editables con PATCH
      const editable = ['name', 'code', 'address', 'municipality', 'province',
        'lat', 'lng', 'type', 'status', 'priority',
        'owner_name', 'owner_phone', 'owner_email', 'owner_type',
        'current_rent', 'contract_end', 'contract_signed_at', 'contract_end_max',
        'superficie_m2', 'fianza_eur', 'periodicidad_pago', 'via_pago',
        'contract_type', 'revalorization_month', 'catastro_ref', 'notes'];
      const payload = {};
      editable.forEach((k) => { if (site[k] !== undefined) payload[k] = site[k]; });
      // Coerce numeric
      ['lat', 'lng', 'current_rent', 'superficie_m2', 'fianza_eur'].forEach((k) => {
        if (payload[k] !== '' && payload[k] !== null && payload[k] !== undefined) {
          const n = Number(payload[k]); if (!Number.isNaN(n)) payload[k] = n;
        }
      });
      const { data } = await api.patch(`/sites/${id}`, payload);
      onSaved(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="site-editor-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-black tracking-widest text-[#A3FF12]">EDITAR EMPLAZAMIENTO</div>
            <h2 className="text-lg sm:text-xl font-black text-white mt-0.5 truncate">
              {site.code || site.name || 'Nuevo'}
              {site.alt_code && <span className="text-white/40 text-sm ml-2">· {site.alt_code}</span>}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1.5"><X size={22}/></button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Código interno" k="code" value={site.code} onChange={setField}/>
          <F label="Nombre" k="name" value={site.name} onChange={setField}/>
          <T label="Dirección" k="address" value={site.address} onChange={setField}/>
          <F label="Municipio" k="municipality" value={site.municipality} onChange={setField}/>
          <F label="Provincia" k="province" value={site.province} onChange={setField}/>
          <F label="Ref. catastral" k="catastro_ref" value={site.catastro_ref} onChange={setField}/>
          <F label="Latitud" k="lat" type="number" step="0.000001" value={site.lat} onChange={setField}/>
          <F label="Longitud" k="lng" type="number" step="0.000001" value={site.lng} onChange={setField}/>

          <div className="md:col-span-2 border-t border-white/10 my-2 pt-2 text-[11px] uppercase text-white/50 tracking-widest">Contacto propietario</div>
          <F label="Propietario" k="owner_name" value={site.owner_name} onChange={setField}/>
          <F label="Teléfono" k="owner_phone" value={site.owner_phone} onChange={setField}/>
          <F label="Email" k="owner_email" type="email" value={site.owner_email} onChange={setField}/>
          <F label="Tipo propietario" k="owner_type" value={site.owner_type} onChange={setField}/>

          <div className="md:col-span-2 border-t border-white/10 my-2 pt-2 text-[11px] uppercase text-white/50 tracking-widest">Contrato</div>
          <F label="Renta anual (€/año)" k="current_rent" type="number" step="0.01" value={site.current_rent} onChange={setField}/>
          <F label="Fianza (€)" k="fianza_eur" type="number" step="0.01" value={site.fianza_eur} onChange={setField}/>
          <F label="Fecha firma" k="contract_signed_at" type="date" value={site.contract_signed_at} onChange={setField}/>
          <F label="Fecha vencimiento" k="contract_end" type="date" value={site.contract_end} onChange={setField}/>
          <F label="Fecha vencimiento máxima" k="contract_end_max" type="date" value={site.contract_end_max} onChange={setField}/>
          <F label="Tipo contrato" k="contract_type" value={site.contract_type} onChange={setField}/>
          <F label="Superficie (m²)" k="superficie_m2" type="number" step="0.01" value={site.superficie_m2} onChange={setField}/>
          <F label="Mes revalorización" k="revalorization_month" value={site.revalorization_month} onChange={setField}/>
          <F label="Periodicidad" k="periodicidad_pago" value={site.periodicidad_pago} onChange={setField}/>
          <F label="Vía de pago" k="via_pago" value={site.via_pago} onChange={setField}/>

          <div className="md:col-span-2 border-t border-white/10 my-2 pt-2 text-[11px] uppercase text-white/50 tracking-widest">Estado</div>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Estado</span>
            <select value={site.status ?? 'prospeccion'} onChange={(e) => setField('status', e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="prospeccion">Prospección</option>
              <option value="negociacion">Negociación</option>
              <option value="firmado">Firmado</option>
              <option value="descartado">Descartado</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Prioridad</span>
            <select value={site.priority ?? 'L3'} onChange={(e) => setField('priority', e.target.value)}
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="L1">L1</option><option value="L2">L2</option><option value="L3">L3</option>
            </select>
          </label>

          <T label="Notas" k="notes" rows={3} value={site.notes} onChange={setField}/>
        </div>

        {err && <div className="mx-5 mb-3 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded-lg text-sm">{err}</div>}
        <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm font-bold px-4 py-2">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-site"
            className="bg-[#A3FF12] text-black font-black text-sm px-4 py-2 rounded-lg hover:bg-[#b8ff3a] disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
};

export default Emplazamientos;
